import { useMemo, useState } from 'react';
import { usePhoneCrumb } from '../lib/usePhoneCrumb';
import { Card, Chip, FilterRow } from '@/components/kit';
import { useShellChrome } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { monthChip, monthName, monthRange, num, shiftMonth, lastCompleteMonth, words } from '../lib/scope';
import { exportCsv } from '@/pages/roles/lib/exportCsv';
import { billedOf, useInvoices, usePatientRoster } from '../lib/useManagerData';
import { ManagerSurface } from '../components/ManagerSurface';
import { ManagerMenu } from '../components/ManagerMenu';

/**
 * Clinic manager · Trends — `g_trends.dc.html`.
 *
 * MONTHS AGAINST MONTHS, FULL WIDTH, NO PANE. Every other report reads ONE
 * month; this one reads a span, so it is the only screen in the surface whose
 * filter row selects a SPAN rather than a month. There is nothing to dock:
 * a month is not a selection, it is a column (Law 4).
 *
 * ONE READ, NOT SIX. The naive shape is a `useInvoices` per month — six to
 * twelve requests on mount, against an API throttled at 20/minute per IP. The
 * span is fetched once and bucketed here, which is also the only way the
 * collection rate and the revenue bar can be guaranteed to be the same
 * arithmetic over the same rows.
 *
 * "MAIE READS THE TREND" IS COMPUTED, NOT GENERATED. The board's own caption
 * under that card says so — "computed deltas and streaks, phrased — never
 * invented". Every clause is a fact about the numbers above it (the best month,
 * the delta against its neighbour, the length of the current streak, the worst
 * month) put into a sentence. No model is called, and no clause is printed
 * unless the fact behind it is there.
 */

type Span = 'six' | 'twelve' | 'pair';

/** The months a span covers, oldest first, ending on the last COMPLETE month. */
function spanMonths(span: Span, end: string): string[] {
  const n = span === 'six' ? 6 : span === 'twelve' ? 12 : 2;
  return Array.from({ length: n }, (_, i) => shiftMonth(end, -(n - 1 - i)));
}

interface MonthFigure {
  key: string;
  /** "Mar" — the axis label. */
  label: string;
  collected: number;
  billed: number;
  /** collected / billed, as a whole percent. `null` when nothing was billed. */
  rate: number | null;
  newPatients: number;
}

/** "124k" / "210.5k" / "940" — the board's own axis figure. */
function compact(n: number): string {
  if (n < 1000) return num(n);
  const k = n / 1000;
  return `${k >= 100 ? Math.round(k * 10) / 10 : Math.round(k)}k`.replace('.0k', 'k');
}

export function ManagerTrendsPage() {
  const [span, setSpan] = useState<Span>('six');
  const [query, setQuery] = useState('');

  /* THE SPAN ENDS ON THE LAST COMPLETE MONTH, for the same reason every other
     report opens on it: a month still being lived is a short column beside
     eleven full ones, and it reads as a collapse rather than as a month that
     has three weeks left in it. */
  const end = lastCompleteMonth();
  const months = useMemo(() => spanMonths(span, end), [span, end]);
  const range = useMemo(
    () => ({ start: monthRange(months[0]).start, end: monthRange(months[months.length - 1]).end }),
    [months],
  );

  const invoices = useInvoices(range);
  const roster = usePatientRoster();
  /* Until the bills are in, every month is "0" — which reads as a month with no money in it. */
  const counting = invoices.isLoading;

  usePageMeta({ title: 'Trends' });

  const figures: MonthFigure[] = useMemo(() => {
    const byMonth = new Map<string, { collected: number; billed: number }>();
    for (const m of months) byMonth.set(m, { collected: 0, billed: 0 });
    for (const inv of invoices.page.rows) {
      /* `date` is a Postgres `date` column and arrives as `YYYY-MM-DD`
         (BRIEF §4.6) — sliced, never parsed into a Date, or a bill on the
         first of a month lands in the previous one east of UTC. */
      const key = String(inv.date ?? '').slice(0, 7);
      const bucket = byMonth.get(key);
      if (!bucket) continue;
      bucket.collected += Number(inv.amountPaid || 0);
      bucket.billed += billedOf(inv);
    }
    return months.map((m) => {
      const b = byMonth.get(m)!;
      return {
        key: m,
        label: monthChip(m).split(' ')[0],
        collected: b.collected,
        billed: b.billed,
        rate: b.billed > 0 ? Math.round((b.collected / b.billed) * 100) : null,
        newPatients: roster.days.filter((d) => d.slice(0, 7) === m).length,
      };
    });
  }, [months, invoices.page.rows, roster.days]);

  const maxCollected = Math.max(1, ...figures.map((f) => f.collected));
  const maxPatients = Math.max(1, ...figures.map((f) => f.newPatients));
  const rated = figures.filter((f) => f.rate !== null);
  const bestRate = rated.length ? Math.max(...rated.map((f) => f.rate!)) : null;
  const worstRate = rated.length ? Math.min(...rated.map((f) => f.rate!)) : null;

  const first = figures[0];
  const last = figures[figures.length - 1];
  const best = figures.reduce((a, b) => (b.collected > a.collected ? b : a), figures[0]);
  const worst = figures.reduce((a, b) => (b.collected < a.collected ? b : a), figures[0]);

  /* THE COLLECTION-RATE CAPTION. Two computed facts, or one, or nothing —
     never a shape with a hole in it. */
  const rateLine = useMemo(() => {
    if (counting) return 'counting…';
    if (!rated.length) return 'nothing was billed in this span, so there is no rate to read';
    const latest = rated[rated.length - 1];
    const here = `${latest.rate}% in ${monthName(latest.key)}`;
    if (rated.length === 1) return `${here} — the only month in this span with bills in it`;
    const lowest = rated.find((f) => f.rate === worstRate)!;
    /* THE DIP CLAUSE ONLY EXISTS IF THERE IS A DIP TO BE PAST. When the lowest
       month IS the latest one, "steady climb since the August dip · 76% in
       August" names August twice and says the opposite of what happened. */
    if (lowest.key === latest.key) return `down to its lowest — ${here}`;
    return latest.rate! > lowest.rate!
      ? `steady climb since the ${monthName(lowest.key)} dip · ${here}`
      : `lowest in ${monthName(lowest.key)} · ${here}`;
  }, [rated, worstRate, counting]);

  /* THE NEW-PATIENT CAPTION — the span's two ends and what happened between
     them, in the board's own shape ("Mar 31 → Aug 87 — …"). */
  const patientLine = useMemo(() => {
    if (!first || !last) return '';
    const ends = `${first.label} ${num(first.newPatients)} → ${last.label} ${num(last.newPatients)}`;
    const total = figures.reduce((s, f) => s + f.newPatients, 0);
    if (!total) return `${ends} — nobody new was registered in this span`;
    /* Read the LAST step, not the two ends: a span that opens on a quiet month and
       ends on a busy one is not "growing, month on month" if the months between are empty. */
    const prev = figures[figures.length - 2];
    if (prev && last.newPatients > prev.newPatients) return `${ends} — up from ${num(prev.newPatients)} in ${prev.label}`;
    if (prev && last.newPatients < prev.newPatients) return `${ends} — down from ${num(prev.newPatients)} in ${prev.label}`;
    return `${ends} — flat across the span`;
  }, [first, last, figures]);

  /* THE READING. Each clause is a fact about the columns above it. */
  const reading = useMemo(() => {
    if (counting || !figures.length || !last) return [];
    /* A SPAN WITH NO MONEY IN IT HAS NO TREND. Without this, the best-month
       clause still fires and says "March is the strongest month in this span —
       EGP 0 collected", which is arithmetic dressed as a finding. */
    if (figures.every((f) => f.collected === 0)) return [];
    const clauses: string[] = [];
    const bestIsLast = best.key === last.key;
    const prev = figures[figures.length - 2];
    if (bestIsLast && prev && prev.collected > 0) {
      const pct = Math.round(((last.collected - prev.collected) / prev.collected) * 100);
      clauses.push(
        `${monthName(last.key)} is your best month yet — EGP ${num(last.collected)} collected, ${pct >= 0 ? '▲' : '▼'}${Math.abs(pct)}% on ${monthName(prev.key)}.`,
      );
    } else {
      clauses.push(
        `${monthName(best.key)} is the strongest month in this span — EGP ${num(best.collected)} collected.`,
      );
    }
    /* A STREAK IS COUNTED, NOT ASSERTED. How many months in a row the money
       rose, ending on the last one. */
    let streak = 0;
    for (let i = figures.length - 1; i > 0; i -= 1) {
      if (figures[i].collected > figures[i - 1].collected) streak += 1; else break;
    }
    if (streak >= 2) {
      /* SPELLED, NOT PRINTED. The canvas writes small counts as words
         ("twenty days in", "fourteen working days" on `g_repzero`, "three
         months straight" here); a numeral in the middle of a sentence is a
         different voice from the one the boards are written in. */
      clauses.push(`Collections have risen ${words(streak)} months straight.`);
    }
    if (bestRate !== null && worstRate !== null && bestRate !== worstRate) {
      clauses.push(
        `The collection rate runs ${worstRate}% to ${bestRate}%, and it is ${last.rate ?? worstRate}% now.`,
      );
    }
    if (worst.key !== last.key && worst.collected < best.collected) {
      clauses.push(`${monthName(worst.key)} was the quietest — plan capacity around it next year.`);
    }
    return clauses;
  }, [counting, figures, last, best, worst, bestRate, worstRate]);

  const spans: Array<{ key: Span; label: string }> = [
    { key: 'six', label: 'Last 6 months' },
    { key: 'twelve', label: '12 months' },
    { key: 'pair', label: `${monthChip(end).split(' ')[0]} vs ${monthChip(shiftMonth(end, -1)).split(' ')[0]}` },
  ];

  /* THE SEARCH NARROWS THE COLUMNS. "Search a month, a metric…" is the board's
     placeholder, so it has to do both: a month name keeps that column, a metric
     name keeps that card. A field that filters nothing is §8's dead control
     wearing a magnifying glass. */
  const q = query.trim().toLowerCase();
  const cardMatches = (title: string) => !q || title.toLowerCase().includes(q)
    || figures.some((f) => `${f.label} ${monthName(f.key)}`.toLowerCase().includes(q));
  const monthMatches = (f: MonthFigure) =>
    !q || `${f.label} ${monthName(f.key)}`.toLowerCase().includes(q)
    || 'revenue collection rate new patients'.includes(q);
  const shownMonths = figures.filter(monthMatches);

  const crumb = usePhoneCrumb('Reports', 'Trends');
  useShellChrome(
    {
      raw: true,
      ...crumb,
      phoneMenu: (close) => <ManagerMenu door="reports" onClose={close} />,
      title: 'Trends',
      sub: 'months against months · all branches',
      search: 'Search a month, a metric…',
      searchValue: query,
      onSearchChange: setQuery,
      primary: {
        label: 'Export',
        onClick: () =>
          exportCsv(
            `trends-${months[0]}-to-${months[months.length - 1]}`,
            ['Month', 'Collected', 'Billed', 'Collection rate', 'New patients'],
            figures.map((f) => [
              f.key, String(Math.round(f.collected)), String(Math.round(f.billed)),
              f.rate === null ? 'no bills' : `${f.rate}%`, String(f.newPatients),
            ]),
          ),
      },
      // NO PANE — a column is not a selection (Law 4).
    },
    [span, query, figures.length, Math.round(last?.collected ?? 0), reading.length],
  );

  return (
    <div className="maie desk">
      <ManagerSurface />
      <div className="dcontent">
        <FilterRow>
          {spans.map((s) => (
            <Chip
              key={s.key}
              kind={span === s.key ? 'brand' : 'outline'}
              small
              className={span === s.key ? 'w6' : undefined}
              onClick={span === s.key ? undefined : () => setSpan(s.key)}
            >
              {s.label}
            </Chip>
          ))}
          <span className="t-cap mut" style={{ padding: '0 4px' }}>·</span>
          <span className="t-sec mut">all branches</span>
        </FilterRow>

        <div className="cm-split cm-trends" style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
          <div style={{ flex: 1.4, minWidth: 0 }}>
            <div style={{ order: 0 }}>
            <Card>
              <p className="t-ovl mut mb8">Revenue · EGP</p>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 150, marginTop: 6 }}>
                {shownMonths.map((f) => (
                  <div key={f.key}
                    style={{ flex: 1, minWidth: 0, alignSelf: 'stretch', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                    <p className="t-cap w6">{counting ? '…' : compact(f.collected)}</p>
                    {/* the bar's % height needs a definite box: this track is the flex:1 remainder of the column */}
                    <div style={{ flex: 1, width: '100%', minHeight: 0, display: 'flex', alignItems: 'flex-end' }}>
                      <div
                        role="img"
                        aria-label={`${monthName(f.key)}: EGP ${num(f.collected)} collected`}
                        style={{
                          width: '100%',
                          background: f.collected === maxCollected ? 'var(--brand-500)' : 'var(--brand-300)',
                          height: `${maxCollected > 0 ? Math.max(2, Math.round((f.collected / maxCollected) * 100)) : 2}%`,
                          borderRadius: '4px 4px 0 0',
                        }}
                      />
                    </div>
                    <p className="t-cap mut">{f.label}</p>
                  </div>
                ))}
              </div>
              {shownMonths.length === 0 ? (
                <p className="t-sec mut">No month in this span matches that.</p>
              ) : null}
            </Card>
            </div>

            {cardMatches('New patients') ? (
              <div style={{ order: 2 }}>
              <Card>
                <p className="t-ovl mut mb8">New patients / month</p>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 76, marginTop: 6 }}>
                  {shownMonths.map((f) => (
                    <div key={f.key} style={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center' }}>
                      <span className="t-cap mut num" style={{ lineHeight: 1 }}>{num(f.newPatients)}</span>
                      <div
                        role="img"
                        aria-label={`${monthName(f.key)}: ${f.newPatients} new`}
                        style={{
                          width: '100%',
                          background: f.newPatients === maxPatients && maxPatients > 0
                            ? 'var(--success-600)' : 'var(--success-100)',
                          height: `${Math.max(2, Math.round((f.newPatients / maxPatients) * 78))}%`,
                          borderRadius: '3px 3px 0 0',
                        }}
                      />
                    </div>
                  ))}
                </div>
                <p className="t-cap mut mt8">{patientLine}</p>
              </Card>
              </div>
            ) : null}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ order: 1 }}>
            <Card>
              <p className="t-ovl mut mb8">Collection rate</p>
              <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                {shownMonths.map((f) => (
                  <div key={f.key} style={{ flex: 1, textAlign: 'center' }}>
                    <p className="t-strong" style={{
                      color: f.rate === null ? 'var(--ink-400)'
                        : f.rate === worstRate && bestRate !== worstRate ? 'var(--danger-600)'
                          : f.rate === bestRate && bestRate !== worstRate ? 'var(--success-600)'
                            : 'var(--ink-400)',
                    }}>
                      {f.rate === null ? '—' : `${f.rate}%`}
                    </p>
                    <p className="t-cap mut">{f.label}</p>
                  </div>
                ))}
              </div>
              <p className="t-cap mut mt8">{rateLine}</p>
            </Card>
            </div>

            <div style={{ order: 3 }}>
            <Card kind="accent">
              <p className="t-sec w6 brand">MAIE reads the trend</p>
              <p className="t-sec brand mt4" style={{ lineHeight: 1.6 }}>
                {reading.length ? reading.join(' ') : 'There is no money in this span to read a trend out of.'}
              </p>
              <p className="t-cap mut mt8">computed deltas and streaks, phrased — never invented</p>
            </Card>
            </div>
          </div>
        </div>

        <p className="t-sec mut" style={{ marginTop: 12 }}>
          Six or twelve months back, or any month against any month — {monthName(end)} against
          LAST {monthName(end)} is how seasonality is read. Every chart obeys the same selector.
        </p>
      </div>
    </div>
  );
}
